import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Home from "./components/Home";
import Quotes from "./components/Quotes";
import ClientDetail from "./components/ClientDetail";
import Login from "./Login";
import Navbar from "./Navbar";
import PaymentDetail from "./components/PaymentDetail";
import Homepage from "./components/Homepage";
import VendorQuote from "./components/VendorQuote";
import SiteProgress from "./components/SiteProgress";
import VendorEstimate from "./components/VendorEstimate";
import VendorRateForm from "./components/VendorRateForm";
import Drawings3D from "./components/Drawings3D/Drawings3D";
import PaymentRequestPage from "./components/PaymentRequestPage";
import AfterBooking from "./components/AfterBooking";
import CustomerQuoteBuilder from "./components/CustomerQuoteBuilder";
import SalesDashboard from "./components/SalesDashboard";
import LeadsList from "./components/LeadsList";
import LeadRecord from "./components/LeadRecord";
import AdminMasterItems from "./pages/AdminMasterItems";
import AdminDashboard from "./pages/AdminDashboard";
import UserPage from "./pages/UserPage";
import VendorPO from "./components/VendorPO";
import VendorPODetail from "./components/VendorPODetail";
import RoleManagement from "./pages/RoleManagement";
import AdminSidebar from "./pages/AdminSidebar";
import RecceModule from "./components/RecceModule";

import Finance from "./Finance/Finance";
import FinanceEntry from "./Finance/FinanceEntry";

import FinanceSalesDashboard from "./Finance/Sales/SalesDashboard";
import SalesMasterPage from "./Finance/Sales/SalesMasterPage";
import QuotationPage from "./Finance/Sales/QuotationPage";
import ProformaInvoicePage from "./Finance/Sales/ProformaInvoicePage";
import InvoicePage from "./Finance/Sales/InvoicePage";
import FinanceReport from "./components/FinanceReport";
const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in";

// Height of the phone status bar (time, battery, network icons).
// It is 0 on a laptop, so nothing changes there.
const SAFE_TOP = "var(--safe-area-inset-top, env(safe-area-inset-top, 0px))";

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || null;
  } catch {
    return null;
  }
};

const getStoredRole = () => {
  const user = getStoredUser();

  return (user?.role || localStorage.getItem("userRole") || "").toLowerCase();
};

const ProtectedRoute = ({ allowedRoles, children }) => {
  const token = localStorage.getItem("token");
  const role = getStoredRole();

  if (!token) return <Navigate to="/" replace />;

  if (allowedRoles && !allowedRoles.includes(role)) {
    if (role === "admin") return <Navigate to="/admin-dashboard" replace />;

    if (role === "sales_manager" || role === "marketing") {
      return <Navigate to="/sales-dashboard" replace />;
    }

    return <Navigate to="/" replace />;
  }

  return children;
};

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const isVendorPublicPage = location.pathname.startsWith("/vendor-rate-form/");

  const token = localStorage.getItem("token");
  const userRole = getStoredRole();

  const isAdmin = userRole === "admin";
  const isSalesUser = userRole === "sales_manager" || userRole === "marketing";

  const [isAuthenticated, setIsAuthenticated] = useState(!!token);

  const [projects, setProjects] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("projects")) || [];
    } catch {
      return [];
    }
  });

  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(
    localStorage.getItem("selectedProjectId"),
  );

  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [filterRoute, setFilterRoute] = useState(
    localStorage.getItem("filterRoute") || "/before-booking",
  );

  // The sidebar is reset only when the screen really switches between
  // phone and laptop size. Opening the phone keyboard also fires "resize"
  // (the height changes), and that used to close the sidebar while typing.
  const wasMobileRef = useRef(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      if (mobile !== wasMobileRef.current) {
        wasMobileRef.current = mobile;
        setIsSidebarOpen(!mobile);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // On a phone, close the project list whenever the page changes
  // (for example after choosing a project or going Home).
  useEffect(() => {
    if (isMobile) setIsSidebarOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const fetchProjects = async (projectIdToSelect) => {
    const currentToken = localStorage.getItem("token");
    if (!currentToken) return;

    try {
      const cachedProjects = localStorage.getItem("projects");

      if (cachedProjects) {
        const parsed = JSON.parse(cachedProjects);
        setProjects(parsed);

        if (projectIdToSelect) {
          const match = parsed.find((p) => p._id === projectIdToSelect);
          if (match) setSelectedProject(match);
        }
      }

      const res = await axios.get(`${BASE_URL}/api/projects`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      const freshProjects = res.data || [];

      setProjects(freshProjects);
      localStorage.setItem("projects", JSON.stringify(freshProjects));

      if (projectIdToSelect) {
        const match = freshProjects.find((p) => p._id === projectIdToSelect);
        if (match) setSelectedProject(match);
      }
    } catch (err) {
      console.error("Project fetch error:", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchProjects(localStorage.getItem("selectedProjectId"));
    }
  }, [token]);

  useEffect(() => {
    if (projects.length > 0 && selectedProjectId) {
      const foundProject = projects.find((p) => p._id === selectedProjectId);
      if (foundProject) setSelectedProject(foundProject);
    }
  }, [projects, selectedProjectId]);

  const addProject = async (newProject) => {
    const currentToken = localStorage.getItem("token");

    await axios.post(`${BASE_URL}/api/projects`, newProject, {
      headers: {
        Authorization: `Bearer ${currentToken}`,
      },
    });

    fetchProjects(selectedProjectId);
  };

  const handleProjectSelect = (project, fromRoute) => {
    setSelectedProject(project);
    setSelectedProjectId(project._id);
    setFilterRoute(fromRoute);

    localStorage.setItem("selectedProjectId", project._id);
    localStorage.setItem("filterRoute", fromRoute);

    if (isMobile) setIsSidebarOpen(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsAuthenticated(false);
    setProjects([]);
    setSelectedProject(null);
    setSelectedProjectId(null);
  };

  // ☰ button in the top bar.
  // Admin  -> opens the admin menu (AdminSidebar listens for this event).
  //           Exception: on /projects it opens the project list instead.
  // Others -> opens / closes the project list (Sidebar).
  const handleMenuToggle = () => {
    if (isAdmin && location.pathname !== "/projects") {
      window.dispatchEvent(new Event("admin-sidebar-toggle"));
      return;
    }

    setIsSidebarOpen((prev) => !prev);
  };

  // On a phone the admin menu is available on every admin page.
  // (The dashboard already has its own AdminSidebar inside it.)
  const showAdminDrawer =
    isAdmin && isMobile && location.pathname !== "/admin-dashboard";

  // ✅ PUBLIC VENDOR FORM - no login, no navbar, no sidebar
  if (isVendorPublicPage) {
    return (
      <Routes>
        <Route path="/vendor-rate-form/:token" element={<VendorRateForm />} />
      </Routes>
    );
  }

  if (!isAuthenticated) {
    return (
      <Login
        onLoginSuccess={() => {
          setIsAuthenticated(true);
          fetchProjects(localStorage.getItem("selectedProjectId"));
        }}
      />
    );
  }

  return (
    <div style={{ fontFamily: "Calibri, sans-serif" }}>
      <Navbar
        onLogout={handleLogout}
        isMobile={isMobile}
        toggleSidebar={handleMenuToggle}
        showMenuButton={!isSalesUser}
      />

      {showAdminDrawer && <AdminSidebar />}

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          // Navbar (70px) + the phone status-bar height (0 on a laptop)
          paddingTop: `calc(70px + ${SAFE_TOP})`,
          boxSizing: "border-box",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {((!isSalesUser && !isAdmin) ||
          (isAdmin && location.pathname === "/projects")) &&
          (location.pathname !== "/" || isMobile) &&
          !(isMobile && location.pathname === "/before-booking") &&
          !location.pathname.toLowerCase().startsWith("/finance") &&
          isSidebarOpen && (
            <>
              {/* Phone: dark layer behind the list. Tap it to close the list. */}
              {isMobile && (
                <div
                  onClick={() => setIsSidebarOpen(false)}
                  aria-hidden="true"
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1100,
                    backgroundColor: "rgba(15, 23, 42, 0.45)",
                  }}
                />
              )}

            <div
              style={{
                width: isMobile ? "min(320px, 86vw)" : "300px",
                flexShrink: 0,
                backgroundColor: "#fff",
                borderRight: "1px solid #e0e0e0",
                overflowY: "auto",
                // On a phone the project list slides over the page (drawer)
                ...(isMobile
                  ? {
                      position: "fixed",
                      top: `calc(70px + ${SAFE_TOP})`,
                      bottom: 0,
                      left: 0,
                      zIndex: 1200,
                      boxShadow: "8px 0 30px rgba(15, 23, 42, 0.28)",
                    }
                  : {}),
              }}
            >
              <Sidebar
                projects={projects}
                onSelectProject={handleProjectSelect}
                onAddProject={addProject}
                filterRoute={filterRoute}
                selectedProjectId={selectedProjectId}
              />
            </div>
            </>
          )}

        <div
          style={{
            flex: 1,
            minHeight: 0,
            padding: isMobile ? "10px" : "20px",
            overflowY: "auto",
            backgroundColor: "#f5f7fa",
          }}
        >
          <Routes>
            <Route
              path="/"
              element={
                isAdmin ? (
                  <Navigate to="/admin-dashboard" replace />
                ) : isSalesUser ? (
                  <Navigate to="/sales-dashboard" replace />
                ) : (
                  <Homepage setSelectedProject={setSelectedProject} />
                )
              }
            />

            <Route
              path="/admin-dashboard"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminDashboard
                    onSelectProject={(project) =>
                      handleProjectSelect(project, "/after-booking")
                    }
                  />
                </ProtectedRoute>
              }
            />

            <Route
              path="/roles"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <RoleManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/master-items"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminMasterItems />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin-master-items"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminMasterItems />
                </ProtectedRoute>
              }
            />

            <Route
              path="/projects"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                 <AfterBooking onSelectProject={handleProjectSelect} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/users"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <UserPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/sales-dashboard"
              element={
                <ProtectedRoute allowedRoles={["sales_manager", "marketing"]}>
                  <SalesDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/leads"
              element={
                <ProtectedRoute allowedRoles={["sales_manager", "marketing"]}>
                  <LeadsList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/leads/:id"
              element={
                <ProtectedRoute allowedRoles={["sales_manager", "marketing"]}>
                  <LeadRecord />
                </ProtectedRoute>
              }
            />

            <Route
              path="/before-booking"
              element={
                isMobile && !isAdmin && !isSalesUser ? (
                  // Phone: the project list IS the page, with a Home button.
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      height: `calc(100vh - 70px - ${SAFE_TOP} - 20px)`,
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        minHeight: 0,
                        borderRadius: "16px",
                        overflow: "hidden",
                        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.1)",
                      }}
                    >
                      <Sidebar
                        projects={projects}
                        onSelectProject={handleProjectSelect}
                        onAddProject={addProject}
                        filterRoute={filterRoute}
                        selectedProjectId={selectedProjectId}
                      />
                    </div>
                  </div>
                ) : (
                  // Laptop: the list is on the left, this is the empty area.
                  <div
                    style={{
                      display: "grid",
                      placeItems: "center",
                      minHeight: "60vh",
                      color: "#64748b",
                      fontSize: "16px",
                      fontWeight: "600",
                      textAlign: "center",
                    }}
                  >
                    Choose a project from the list on the left.
                  </div>
                )
              }
            />

            <Route path="/after-booking" element={<AfterBooking onSelectProject={handleProjectSelect} />} />

            <Route
              path="/project"
              element={
                selectedProject ? (
                  <Home selectedProject={selectedProject} />
                ) : null
              }
            />

            <Route
              path="/client-detail"
              element={
                selectedProject ? (
                  <ClientDetail selectedProject={selectedProject} />
                ) : null
              }
            />

            <Route
              path="/quote"
              element={
                selectedProject ? <Quotes project={selectedProject} /> : null
              }
            />

            <Route
              path="/PaymentDetail"
              element={
                selectedProject ? (
                  <PaymentDetail
                    project={selectedProject}
                    refreshProjects={fetchProjects}
                  />
                ) : null
              }
            />

            <Route
              path="/VendorQuote"
              element={
                selectedProject?.hasPayment ? (
                  <VendorQuote project={selectedProject} />
                ) : null
              }
            />

            <Route
              path="/vendor-estimate"
              element={
                selectedProject?.hasPayment ? (
                  <VendorEstimate project={selectedProject} />
                ) : null
              }
            />

            <Route
              path="/SiteProgress"
              element={
                selectedProject?.hasPayment ? (
                  <SiteProgress project={selectedProject} />
                ) : null
              }
            />

            <Route
              path="/recce-module"
              element={
                selectedProject?.hasPayment ? (
                  <RecceModule project={selectedProject} />
                ) : null
              }
            />

            <Route
              path="/3dDrawings"
              element={
                selectedProject?.hasPayment ? (
                  <Drawings3D project={selectedProject} />
                ) : null
              }
            />

            <Route path="/vendor-po" element={<VendorPO />} />

            <Route path="/vendor-po/:vendorId" element={<VendorPODetail />} />

            <Route path="/customer-quote" element={<CustomerQuoteBuilder />} />

            <Route
              path="/payment-request/:projectId"
              element={<PaymentRequestPage />}
            />

            <Route
              path="/Finance"
              element={<Finance selectedProject={selectedProject} />}
            >
              <Route index element={<Navigate to="entry" replace />} />

              <Route
                path="entry"
                element={<FinanceEntry selectedProject={selectedProject} />}
              />

              <Route
                path="sales-dashboard"
                element={
                  <FinanceSalesDashboard selectedProject={selectedProject} />
                }
              />

              <Route
                path="sales-master"
                element={<Navigate to="service-master" replace />}
              />

              <Route
                path="sales-master/service-master"
                element={
                  <SalesMasterPage
                    selectedProject={selectedProject}
                    type="services"
                  />
                }
              />

              <Route
                path="sales-master/client-master"
                element={
                  <SalesMasterPage
                    selectedProject={selectedProject}
                    type="clients"
                  />
                }
              />

              <Route
                path="sales-master/bank-detail-master"
                element={
                  <SalesMasterPage
                    selectedProject={selectedProject}
                    type="banks"
                  />
                }
              />

              <Route
                path="sales-master/terms-conditions"
                element={
                  <SalesMasterPage
                    selectedProject={selectedProject}
                    type="terms"
                  />
                }
              />

              <Route
                path="quotations"
                element={<QuotationPage selectedProject={selectedProject} />}
              />

              <Route
                path="proforma-invoices"
                element={
                  <ProformaInvoicePage selectedProject={selectedProject} />
                }
              />

              <Route
                path="invoices"
                element={<InvoicePage selectedProject={selectedProject} />}
              />
            </Route>
            <Route path="/payment-report" element={<FinanceReport />} />
            <Route
              path="*"
              element={
                isAdmin ? (
                  <Navigate to="/admin-dashboard" replace />
                ) : isSalesUser ? (
                  <Navigate to="/sales-dashboard" replace />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default App;